import { z } from "zod";

export const reviewStateSchema = z.enum(["seed", "pending_review", "approved", "blocked"]);
export const provenanceTypeSchema = z.enum([
  "textbook",
  "owner_confirmed",
  "local_extension",
  "restorative_creation",
]);

export const pageRefSchema = z.object({
  print: z.string().min(1),
  pdf: z.string().min(1),
});

export const sourceSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  organization: z.string().min(1),
  url: z.url().optional(),
  provenanceType: provenanceTypeSchema,
  reviewState: reviewStateSchema,
  note: z.string().optional(),
});

export const assetSchema = z
  .object({
    id: z.string().regex(/^AST-[A-Z0-9-]+$/),
    title: z.string().min(1),
    sceneIds: z.array(z.string().regex(/^M[1-6]S[1-4]$/)).min(1),
    kind: z.enum(["programmatic_3d", "programmatic_2_5d", "interface", "media"]),
    fileRefs: z.array(z.string().min(1)).min(1),
    sourceRefs: z.array(z.string().min(1)),
    rightsStatus: z.enum(["original", "licensed", "public_network_pending_evidence", "blocked"]),
    originality: z.enum([
      "original_code",
      "original_media",
      "adapted",
      "third_party",
      "ai_assisted",
    ]),
    altZh: z.string().min(1),
    restorativeCreation: z.boolean(),
    visibleLabel: z.string().min(1).optional(),
    reviewState: reviewStateSchema,
  })
  .superRefine((asset, context) => {
    if (asset.restorativeCreation && asset.visibleLabel !== "复原性创作·非档案影像") {
      context.addIssue({
        code: "custom",
        message: "复原性创作资产必须使用统一可见标签。",
        path: ["visibleLabel"],
      });
    }
    if (
      asset.rightsStatus === "original" &&
      !asset.originality.startsWith("original_") &&
      asset.originality !== "ai_assisted"
    ) {
      context.addIssue({
        code: "custom",
        message: "标为原创权利状态的资产必须使用原创或AI辅助原创归属类型。",
        path: ["originality"],
      });
    }
  });

export const contentBlockSchema = z.object({
  id: z.string().min(1),
  sceneId: z.string().regex(/^M[1-6]S[1-4]$/),
  order: z.number().int().positive(),
  kind: z.enum(["lecture", "summary", "local_extension", "archive_witness"]),
  title: z.string().min(1),
  bodyZh: z.string().min(1),
  required: z.boolean(),
  provenanceType: provenanceTypeSchema,
  sourceRefs: z.array(z.string()),
  assetRefs: z.array(z.string().regex(/^AST-[A-Z0-9-]+$/)).optional(),
  pageRefs: z.array(pageRefSchema),
  reviewState: reviewStateSchema,
  visibleLabel: z.string().optional(),
});

const taskBaseSchema = z.object({
  id: z.string().regex(/^T(0[1-9]|1[0-9])$/),
  order: z.number().int().min(1).max(19),
  sceneId: z.string().regex(/^M[1-6]S[1-4]$/),
  title: z.string().min(1),
  stem: z.string().min(1),
  hint: z.string().min(1),
  explanation: z.string().min(1),
  pageRefs: z.array(pageRefSchema),
  reviewState: reviewStateSchema,
  formal: z.literal(true),
});

export const choiceOptionSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  feedback: z.string().min(1),
  misconceptionCode: z.string().optional(),
});

export const singleChoiceTaskSchema = taskBaseSchema.extend({
  type: z.literal("single_choice"),
  presentation: z.enum(["cards", "select"]).default("cards"),
  options: z.array(choiceOptionSchema).min(2),
  correctOptionId: z.string().min(1),
});

export const dragSortTaskSchema = taskBaseSchema.extend({
  type: z.literal("drag_sort"),
  items: z.array(z.object({ id: z.string(), text: z.string() })).min(2),
  correctOrder: z.array(z.string()).min(2),
});

export const dragMatchTaskSchema = taskBaseSchema.extend({
  type: z.literal("drag_match"),
  left: z.array(z.object({ id: z.string(), text: z.string() })).min(2),
  right: z.array(z.object({ id: z.string(), text: z.string() })).min(2),
  correctPairs: z.record(z.string(), z.string()),
});

export const taskSchema = z
  .discriminatedUnion("type", [singleChoiceTaskSchema, dragSortTaskSchema, dragMatchTaskSchema])
  .superRefine((task, context) => {
    if (task.type === "single_choice") {
      const optionIds = task.options.map((option) => option.id);
      if (new Set(optionIds).size !== optionIds.length) {
        context.addIssue({ code: "custom", message: "选择题选项ID必须唯一。", path: ["options"] });
      }
      if (!optionIds.includes(task.correctOptionId)) {
        context.addIssue({
          code: "custom",
          message: "选择题正确答案必须引用现有选项。",
          path: ["correctOptionId"],
        });
      }
      return;
    }

    if (task.type === "drag_sort") {
      const itemIds = task.items.map((item) => item.id);
      if (
        new Set(itemIds).size !== itemIds.length ||
        task.correctOrder.length !== itemIds.length ||
        new Set(task.correctOrder).size !== itemIds.length ||
        task.correctOrder.some((id) => !itemIds.includes(id))
      ) {
        context.addIssue({
          code: "custom",
          message: "排序题正确顺序必须且只能完整引用全部唯一项目。",
          path: ["correctOrder"],
        });
      }
      return;
    }

    const leftIds = task.left.map((item) => item.id);
    const rightIds = task.right.map((item) => item.id);
    const pairEntries = Object.entries(task.correctPairs);
    if (
      new Set(leftIds).size !== leftIds.length ||
      new Set(rightIds).size !== rightIds.length ||
      pairEntries.length !== leftIds.length ||
      pairEntries.some(
        ([leftId, rightId]) => !leftIds.includes(leftId) || !rightIds.includes(rightId),
      ) ||
      new Set(pairEntries.map(([, rightId]) => rightId)).size !== rightIds.length
    ) {
      context.addIssue({
        code: "custom",
        message: "配对题答案必须建立左右唯一项目的一一对应关系。",
        path: ["correctPairs"],
      });
    }
  });

export const sceneSchema = z.object({
  id: z.string().regex(/^M[1-6]S[1-4]$/),
  globalOrder: z.number().int().min(1).max(18),
  moduleId: z.string().regex(/^M[1-6]$/),
  moduleOrder: z.number().int().min(1).max(4),
  title: z.string().min(1),
  identity: z.literal("抗战史料研学员"),
  timeLabel: z.string().min(1),
  mainVenue: z.string().min(1),
  archiveSlices: z.array(z.string()),
  taskIds: z.array(z.string()),
  contentBlockIds: z.array(z.string()),
  assetIds: z.array(z.string().regex(/^AST-[A-Z0-9-]+$/)).optional(),
  reviewState: reviewStateSchema,
  availability: z.enum(["flagship", "structured", "seed"]),
});

export const moduleSchema = z.object({
  id: z.string().regex(/^M[1-6]$/),
  order: z.number().int().min(1).max(6),
  title: z.string().min(1),
  printPageRange: z.string().min(1),
  pdfPageRange: z.string().min(1),
  sceneIds: z.array(z.string()).min(2),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});

export type ReviewState = z.infer<typeof reviewStateSchema>;
export type PageRef = z.infer<typeof pageRefSchema>;
export type Source = z.infer<typeof sourceSchema>;
export type Asset = z.infer<typeof assetSchema>;
export type ContentBlock = z.infer<typeof contentBlockSchema>;
export type Task = z.infer<typeof taskSchema>;
export type SingleChoiceTask = z.infer<typeof singleChoiceTaskSchema>;
export type DragSortTask = z.infer<typeof dragSortTaskSchema>;
export type DragMatchTask = z.infer<typeof dragMatchTaskSchema>;
export type Scene = z.infer<typeof sceneSchema>;
export type Module = z.infer<typeof moduleSchema>;
